import { useState } from "react";
import { X, Loader2, Check, AlertTriangle, Sparkles, User } from "lucide-react";
import { api, apiError } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";

/**
 * AssetDemandSignoffModal — opens when a crew member taps a custom job row
 * on the Swing page. Shows the request text + asset + who raised it, then
 * gathers a signature and POSTs `/asset-demands/{id}/sign` to mark the
 * request done. Mirrors the mobile-app flow.
 */
export default function AssetDemandSignoffModal({ demand, onClose, onSigned }) {
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const done = String(demand?.status || "").toLowerCase() === "done";
  const canSubmit = !!signature && !busy && !done;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/asset-demands/${demand.id}/sign`, {
        signature_svg: signature,
        signer_name: signerName.trim() || undefined,
      });
      setMsg({ type: "ok", text: "Signed off." });
      setTimeout(() => onSigned?.(), 600);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="custom-job-signoff-modal">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
        onClick={onClose}
      />
      <div className="relative panel-heavy w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
          >
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted font-black">Custom Job</div>
            <div className="font-display font-black text-lg tracking-tight truncate">
              {demand?.asset_id || "—"}
            </div>
          </div>
          <button
            className="btn btn-ghost btn-xs !py-1 !px-2"
            onClick={onClose}
            data-testid="custom-job-signoff-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          <div className="panel-flat p-3 text-sm text-white/90" data-testid="custom-job-signoff-text">
            {demand?.text || "—"}
          </div>
          {demand?.created_by_name && (
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
              <User className="w-3 h-3" />
              Raised by <span className="text-white font-black">{demand.created_by_name}</span>
            </div>
          )}

          {done ? (
            <div className="pill pill-green !text-xs !py-2 !px-3 justify-start" data-testid="custom-job-already-done">
              <Check className="w-3.5 h-3.5" /> Already signed off by {demand.completed_by_name || "—"}
            </div>
          ) : (
            <>
              <div className="field">
                <label>Signer name <span className="text-muted text-[10px] uppercase tracking-widest">(optional)</span></label>
                <input
                  type="text"
                  className="input"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Full name"
                  data-testid="custom-job-signer-name"
                />
              </div>

              <div className="field">
                <label>Signature</label>
                <SignaturePad
                  onChange={setSignature}
                  label=""
                  testid="custom-job-signature"
                  height={160}
                />
              </div>
            </>
          )}

          {msg && (
            <div
              className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}
              data-testid="custom-job-signoff-msg"
            >
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {msg.text}
            </div>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div className="p-5 flex items-center justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="custom-job-signoff-cancel">
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="custom-job-signoff-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Sign off & complete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
